// Persisted salvage evidence only; processing counters never imply coverage.
const partialOutcomeSelection = `partialOutcome {
  schemaVersion outcome originalDurationMs playbackDurationMs expectedPartCount
  includedLogicalParts
  omittedLogicalParts { index reason permanentSourceLossClaimed }
  missingIntervals { kind from to durationMs clockCandidateOnly }
  completeCoverage normalCompletionAllowed
}`;

const showRecordingPartialOutcome = (role, flag = process.env.REACT_APP_RECORDING_PARTIAL_OUTCOME_CUSTOMER_VISIBLE) =>
  role === 'admin' || flag === 'true';

const durationText = durationMs => {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs < 0) return 'Unavailable';
  const seconds = Math.round(durationMs / 1000);
  const hours = Math.floor(seconds / 3600), minutes = Math.floor((seconds % 3600) / 60), remainder = seconds % 60;
  return [hours ? `${hours}h` : '', minutes ? `${minutes}m` : '', remainder || !seconds ? `${remainder}s` : ''].filter(Boolean).join(' ');
};

const partialOutcomeView = outcome => {
  if (!outcome || outcome.schemaVersion !== 1 || outcome.outcome !== 'partial' || outcome.completeCoverage !== false ||
      typeof outcome.normalCompletionAllowed !== 'boolean' ||
      !Number.isFinite(outcome.originalDurationMs) || outcome.originalDurationMs <= 0 ||
      !Number.isFinite(outcome.playbackDurationMs) || outcome.playbackDurationMs <= 0 ||
      outcome.playbackDurationMs > outcome.originalDurationMs ||
      !Number.isSafeInteger(outcome.expectedPartCount) || outcome.expectedPartCount < 1 || outcome.expectedPartCount > 64 ||
      !Array.isArray(outcome.includedLogicalParts) || !outcome.includedLogicalParts.length ||
      !Array.isArray(outcome.omittedLogicalParts) || !outcome.omittedLogicalParts.length ||
      !Array.isArray(outcome.missingIntervals) || outcome.missingIntervals.length > 64) return null;
  const included = outcome.includedLogicalParts, omitted = outcome.omittedLogicalParts;
  const all = included.concat(omitted.map(part => part?.index));
  if (all.length !== outcome.expectedPartCount || new Set(all).size !== all.length ||
      all.some(index => !Number.isSafeInteger(index) || index < 0 || index >= outcome.expectedPartCount) ||
      omitted.some(part => typeof part.reason !== 'string' || typeof part.permanentSourceLossClaimed !== 'boolean')) return null;
  return {
    playback: durationText(outcome.playbackDurationMs), original: durationText(outcome.originalDurationMs),
    expectedPartCount: outcome.expectedPartCount, included: included.slice().sort((a,b) => a-b),
    omitted: omitted.map(part => ({ index: part.index, reason: part.reason,
      permanentSourceLossClaimed: part.permanentSourceLossClaimed })).sort((a,b) => a.index-b.index),
    hasTimingEstimate: outcome.missingIntervals.some(interval => interval?.clockCandidateOnly === true),
    unavailableSectionDuration: outcome.missingIntervals
      .filter(interval => interval?.kind === 'entire-resumed-section-unavailable-in-audited-sources' && interval.clockCandidateOnly !== true &&
        typeof interval.durationMs === 'number' && Number.isFinite(interval.durationMs) && interval.durationMs > 0)
      .map(interval => durationText(interval.durationMs)),
    normalCompletionAllowed: outcome.normalCompletionAllowed,
  };
};

// Show a safe label, not arbitrary backend reasons, paths or provider messages.
const omittedPartReasonText = reason => ({
  'unavailable-in-audited-sources': 'Unavailable in the audited sources',
  'missing-source': 'Source unavailable', 'source-unavailable': 'Source unavailable',
  'zero-byte': 'Empty source', 'empty-source': 'Empty source',
  'unhealthy-source': 'Unhealthy source', 'decode-failed': 'Source could not be decoded',
  'preprocess-failed': 'Processing failed', 'missing-processed-part': 'Processed part unavailable',
}[reason] || 'Unavailable or unhealthy part');

module.exports = { partialOutcomeSelection, showRecordingPartialOutcome, durationText, partialOutcomeView, omittedPartReasonText };
